// src/scripts/sqlApiHelper/tillsApi.js
import { auth, db } from "@/firebase";
import {
  doc,
  collection,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

/**
 * Firestore model:
 * venues/{venueId}/tills_days/{tradingDateISO}
 * venues/{venueId}/tills_days/{tradingDateISO}/tills/{tillNumber}
 *
 * Optional summaries:
 * venues/{venueId}/till_week_summaries/{weekEndingISO}
 */

/* =========================
   Helpers (keep behaviour stable)
   ========================= */
function must(v, msg) {
  if (!v) throw new Error(msg);
  return v;
}

function asNumber(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

function normLines(lines) {
  const arr = Array.isArray(lines) ? lines : [];
  return arr
    .map((l) => ({
      line_type: String(l?.line_type ?? l?.lineType ?? "").trim(),
      label: String(l?.label ?? "").trim(),
      amount: asNumber(l?.amount, 0),
    }))
    .filter((l) => l.line_type && l.label && Number.isFinite(l.amount));
}

function currentUserMeta() {
  return {
    updated_by_uid: auth.currentUser?.uid ?? null,
    updated_by_email: auth.currentUser?.email ?? null,
  };
}

/* =========================================================
   Save / update tills for a trading date (Firestore batch)
   (keeps old outward response shape)
   ========================================================= */
export async function upsertTillsForTradingDay(payload) {
  must(payload && typeof payload === "object", "Payload is required");

  const venue_id = must(payload.venue_id, "Missing venue_id");
  const week_ending_date = must(payload.week_ending_date, "Missing week_ending_date");
  const trading_date = must(payload.trading_date, "Missing trading_date");
  const tills = payload.tills;

  if (!Array.isArray(tills)) throw new Error("tills must be an array");

  const dayRef = doc(db, "venues", venue_id, "tills_days", trading_date);
  const batch = writeBatch(db);

  // header doc
  batch.set(
    dayRef,
    {
      venue_id,
      trading_date,
      week_ending_date,
      ...currentUserMeta(),
      updated_at: serverTimestamp(),
    },
    { merge: true }
  );

  // till docs
  for (let i = 0; i < tills.length; i++) {
    const t = tills[i] || {};
    const till_number = asNumber(t.till_number ?? t.tillNumber ?? i + 1);
    if (!Number.isFinite(till_number)) throw new Error("Invalid till_number in tills array");

    const declared_total = asNumber(t.declared_total ?? t.declaredTotal ?? 0);
    const counted_total = asNumber(t.counted_total ?? t.countedTotal ?? 0);
    const variance = asNumber(t.variance, counted_total - declared_total);

    const tillRef = doc(dayRef, "tills", String(till_number));

    batch.set(
      tillRef,
      {
        till_number,
        declared_total,
        counted_total,
        variance,
        notes: t.notes != null ? String(t.notes) : null,

        // ✅ store breakdown lines
        lines: normLines(t.lines),

        ...currentUserMeta(),
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
  }

  await batch.commit();

  // Keep the old batch-like response used by the UI
  return {
    ok: true,
    venue_id,
    week_ending_date,
    trading_date,
    rows: tills.map((t, i) => {
      const tn = asNumber(t?.till_number ?? t?.tillNumber ?? i + 1);
      const declared_total = asNumber(t?.declared_total ?? t?.declaredTotal ?? 0);
      const counted_total = asNumber(t?.counted_total ?? t?.countedTotal ?? 0);
      const lines = normLines(t?.lines);

      return {
        id: `${venue_id}:${trading_date}:${tn}`,
        venue_id,
        week_ending_date,
        trading_date,
        till_number: tn,
        declared_total,
        counted_total,
        variance: asNumber(t?.variance, counted_total - declared_total),
        notes: t?.notes ?? null,

        // ✅ match old API shape expectations
        lines,
        till_count_lines: lines, // some consumers used this name
      };
    }),
  };
}

/* =========================================================
   Load tills for a trading date
   (LEGACY SHAPE: mirrors tills-get-trading-day)
   Returns: { ok, venue_id, trading_date, rows:[ {.., lines:[..]} ] }
   ========================================================= */
export async function getTillsForTradingDay({ venue_id, trading_date, week_ending_date }) {
  // week_ending_date is optional (kept for signature compatibility)
  must(venue_id, "Missing venue_id");
  must(trading_date, "Missing trading_date");

  const dayRef = doc(db, "venues", venue_id, "tills_days", trading_date);
  const daySnap = await getDoc(dayRef);

  if (!daySnap.exists()) {
    return { ok: true, venue_id, trading_date, rows: [] };
  }

  const day = daySnap.data() || {};
  const we = day.week_ending_date ?? week_ending_date ?? null;

  const tillsCol = collection(dayRef, "tills");
  const qy = query(tillsCol, orderBy("till_number", "asc"));
  const snap = await getDocs(qy);

  // ✅ flat per-till rows, with lines included
  const rows = snap.docs.map((d) => {
    const t = d.data() || {};
    const lines = Array.isArray(t.lines) ? t.lines : [];

    return {
      id: `${venue_id}:${trading_date}:${d.id}`,
      venue_id,
      week_ending_date: we,
      trading_date,
      till_number: asNumber(t.till_number, asNumber(d.id)),
      declared_total: asNumber(t.declared_total, 0),
      counted_total: asNumber(t.counted_total, 0),
      variance: asNumber(
        t.variance,
        asNumber(t.counted_total, 0) - asNumber(t.declared_total, 0)
      ),
      notes: t.notes ?? null,

      // ✅ this is what your old endpoint attached
      lines,

      // ✅ safety for any consumers expecting this name
      till_count_lines: lines,
    };
  });

  return { ok: true, venue_id, trading_date, rows };
}

/* =========================================================
   Load tills for a date range (inclusive)
   (LEGACY SHAPE: mirrors get-tills-date-range)
   Supabase returned flat rows per till + till_count_lines(*)
   Returns: { ok, venue_id, date_from, date_to, rows:[ {.., till_count_lines:[..]} ] }
   ========================================================= */
export async function getTillsForDateRange({ venue_id, date_from, date_to }) {
  must(venue_id, "Missing venue_id");
  must(date_from, "Missing date_from");
  must(date_to, "Missing date_to");

  const daysCol = collection(db, "venues", venue_id, "tills_days");

  // trading_date stored as "YYYY-MM-DD" so string range works.
  const qDays = query(
    daysCol,
    where("trading_date", ">=", date_from),
    where("trading_date", "<=", date_to),
    orderBy("trading_date", "asc")
  );

  const daySnaps = await getDocs(qDays);

  const rows = [];

  // ✅ return FLAT till rows (not nested day->tills)
  for (const dayDoc of daySnaps.docs) {
    const day = dayDoc.data() || {};
    const trading_date = day.trading_date ?? dayDoc.id;
    const week_ending_date = day.week_ending_date ?? null;

    const tillsCol = collection(dayDoc.ref, "tills");
    const qTills = query(tillsCol, orderBy("till_number", "asc"));
    const tillsSnap = await getDocs(qTills);

    for (const tDoc of tillsSnap.docs) {
      const t = tDoc.data() || {};
      const lines = Array.isArray(t.lines) ? t.lines : [];

      rows.push({
        id: `${venue_id}:${trading_date}:${tDoc.id}`,
        venue_id,
        week_ending_date,
        trading_date,
        till_number: asNumber(t.till_number, asNumber(tDoc.id)),
        declared_total: asNumber(t.declared_total, 0),
        counted_total: asNumber(t.counted_total, 0),
        variance: asNumber(
          t.variance,
          asNumber(t.counted_total, 0) - asNumber(t.declared_total, 0)
        ),
        notes: t.notes ?? null,

        // ✅ match Supabase include join name
        till_count_lines: lines,

        // ✅ extra safety
        lines,
      });
    }
  }

  return { ok: true, venue_id, date_from, date_to, rows };
}

/* =========================================================
   Load till week summaries for a date range (inclusive)
   (LEGACY SHAPE: mirrors till-week-range-get)
   Returns: { ok, venueId, date_from, date_to, rows:[{week_ending_date,payload}] }
   ========================================================= */
export async function getTillWeekSummariesForRange({ venueId, date_from, date_to }) {
  must(venueId, "Missing venueId");
  must(date_from, "Missing date_from");
  must(date_to, "Missing date_to");

  const colRef = collection(db, "venues", venueId, "till_week_summaries");
  const qy = query(
    colRef,
    where("week_ending_date", ">=", date_from),
    where("week_ending_date", "<=", date_to),
    orderBy("week_ending_date", "asc")
  );

  const snap = await getDocs(qy);

  const rows = snap.docs.map((d) => {
    const x = d.data() || {};
    return {
      week_ending_date: x.week_ending_date ?? d.id,
      payload: x.payload ?? null,
    };
  });

  // =========================================================
  // DEBUG LOGGING (payload inspection) — preserved
  // =========================================================
  try {
    const r = Array.isArray(rows) ? rows : [];
    console.log("[till-week-range-get] ok:", true, "rows:", r.length);

    if (r.length) {
      console.log("[till-week-range-get] first.week_ending_date:", r[0]?.week_ending_date);
      console.log("[till-week-range-get] first.payload:", r[0]?.payload);

      for (const one of r) {
        console.log("[till-week-range-get] week:", one?.week_ending_date, "payload:", one?.payload);
      }
    } else {
      console.log("[till-week-range-get] no rows returned");
    }
  } catch (e) {
    console.log("[till-week-range-get] debug logging failed:", e?.message || e);
  }
  // =========================================================

  return { ok: true, venueId, date_from, date_to, rows };
}